import React, { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './Pagination.css';

const DEFAULT_PAGE_SIZE_OPTIONS = [12, 25, 50, 100];

/**
 * @param {number} currentPage - 1-based
 * @param {number} totalPages
 * @param {(page: number) => void} onPageChange
 * @param {number} [totalItems]
 * @param {number} [itemsPerPage]
 * @param {(size: number) => void} [onPageSizeChange]
 * @param {number[]} [pageSizeOptions]
 * @param {boolean|string} [isRtl] - true, or 'rtl' / 'ltr' direction string
 * @param {string} [className]
 * @param {number} [pageJumpMinPages]
 */
function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    totalItems = 0,
    itemsPerPage = 25,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    isRtl = false,
    dir,
    className = '',
    pageJumpMinPages = 2,
}) {
    const { t } = useTranslation();
    const pageJumpId = useId();
    const pageSizeId = useId();

    const rtl = typeof isRtl === 'boolean'
        ? isRtl
        : String(dir || isRtl || '').toLowerCase() === 'rtl';

    const safeTotalPages = Math.max(0, Number(totalPages) || 0);
    const safeCurrentPage = Math.min(
        Math.max(1, Number(currentPage) || 1),
        safeTotalPages || 1
    );
    const safeTotalItems = Math.max(0, Number(totalItems) || 0);
    const safeItemsPerPage = Math.max(1, Number(itemsPerPage) || 1);

    const resolvedPageSizeOptions = useMemo(() => {
        const base =
            Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0
                ? pageSizeOptions
                : DEFAULT_PAGE_SIZE_OPTIONS;
        if (base.includes(safeItemsPerPage)) return base;
        return [...base, safeItemsPerPage].sort((a, b) => a - b);
    }, [pageSizeOptions, safeItemsPerPage]);

    const pageOptions = useMemo(() => {
        const options = [];
        for (let i = 1; i <= safeTotalPages; i += 1) {
            options.push(i);
        }
        return options;
    }, [safeTotalPages]);

    const pageRange = useMemo(() => {
        if (safeTotalItems === 0) return { from: 0, to: 0 };
        const from = (safeCurrentPage - 1) * safeItemsPerPage + 1;
        const to = Math.min(safeCurrentPage * safeItemsPerPage, safeTotalItems);
        return { from, to };
    }, [safeCurrentPage, safeItemsPerPage, safeTotalItems]);

    const showNav = safeTotalPages > 1;
    const showPageJump = showNav && safeTotalPages >= pageJumpMinPages;
    const showPageSize = typeof onPageSizeChange === 'function' && safeTotalItems > 0;
    const showSummary = safeTotalItems > 0;

    if (!showNav && !showSummary) {
        return null;
    }

    const goPrev = () => {
        if (safeCurrentPage > 1) onPageChange(safeCurrentPage - 1);
    };

    const goNext = () => {
        if (safeCurrentPage < safeTotalPages) onPageChange(safeCurrentPage + 1);
    };

    const handlePageJump = (event) => {
        const nextPage = Number.parseInt(event.target.value, 10);
        if (!Number.isFinite(nextPage) || nextPage === safeCurrentPage) return;
        onPageChange(Math.min(Math.max(1, nextPage), safeTotalPages || 1));
    };

    const handlePageSizeChange = (event) => {
        const nextSize = Number.parseInt(event.target.value, 10);
        if (!Number.isFinite(nextSize) || nextSize < 1) return;
        onPageSizeChange(nextSize);
    };

    const pageElements = [...Array(safeTotalPages)].map((_, index) => {
        const pageNumber = index + 1;
        if (
            pageNumber === 1 ||
            pageNumber === safeTotalPages ||
            (pageNumber >= safeCurrentPage - 1 && pageNumber <= safeCurrentPage + 1)
        ) {
            return (
                <button
                    key={pageNumber}
                    type="button"
                    className={`pagination-number ${safeCurrentPage === pageNumber ? 'pagination-number--active' : ''}`}
                    onClick={() => onPageChange(pageNumber)}
                    aria-label={t('menu.paginationGoToPage', { page: pageNumber, defaultValue: 'Go to page {{page}}' })}
                    aria-current={safeCurrentPage === pageNumber ? 'page' : undefined}
                >
                    {pageNumber}
                </button>
            );
        }
        if (pageNumber === safeCurrentPage - 2 || pageNumber === safeCurrentPage + 2) {
            return (
                <span key={pageNumber} className="pagination-ellipsis" aria-hidden>
                    ...
                </span>
            );
        }
        return null;
    });

    return (
        <div className={`pagination-wrap ${className}`.trim()} dir={rtl ? 'rtl' : 'ltr'}>
            {showNav && (
                <nav className="pagination" aria-label={t('menu.paginationNav', { defaultValue: 'Pagination' })}>
                    <button
                        type="button"
                        className="pagination-button"
                        onClick={goPrev}
                        disabled={safeCurrentPage === 1}
                        aria-label={t('menu.paginationPrev', { defaultValue: 'Previous page' })}
                    >
                        {rtl ? <ChevronRight size={20} aria-hidden /> : <ChevronLeft size={20} aria-hidden />}
                    </button>

                    <div className="pagination-numbers" dir="ltr">{pageElements}</div>

                    <button
                        type="button"
                        className="pagination-button"
                        onClick={goNext}
                        disabled={safeCurrentPage === safeTotalPages}
                        aria-label={t('menu.paginationNext', { defaultValue: 'Next page' })}
                    >
                        {rtl ? <ChevronLeft size={20} aria-hidden /> : <ChevronRight size={20} aria-hidden />}
                    </button>
                </nav>
            )}

            {showSummary && (
                <div className="pagination-meta" aria-live="polite">
                    <p className="pagination-summary">
                        <span>{t('menu.paginationShowing', { defaultValue: 'Showing' })}</span>
                        {' '}
                        <strong>
                            {pageRange.from === 0 ? '0' : `${pageRange.from}–${pageRange.to}`}
                        </strong>
                        {' '}
                        <span>{t('menu.paginationOf', { defaultValue: 'of' })}</span>
                        {' '}
                        <strong>{safeTotalItems}</strong>
                        {' '}
                        <span>{t('menu.paginationItems', { defaultValue: 'items' })}</span>
                    </p>

                    {(showPageSize || showPageJump) && (
                        <div className="pagination-meta-actions">
                            {showPageSize && (
                                <label className="pagination-field" htmlFor={pageSizeId}>
                                    <span className="pagination-field-label">
                                        {t('menu.paginationPerPage', { defaultValue: 'Per page' })}
                                    </span>
                                    <select
                                        id={pageSizeId}
                                        className="pagination-field-select"
                                        value={safeItemsPerPage}
                                        onChange={handlePageSizeChange}
                                        aria-label={t('menu.paginationPerPage', { defaultValue: 'Per page' })}
                                    >
                                        {resolvedPageSizeOptions.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            {showPageJump && (
                                <label className="pagination-field" htmlFor={pageJumpId}>
                                    <span className="pagination-field-label">
                                        {t('menu.paginationJumpLabel', { defaultValue: 'Go to page' })}
                                    </span>
                                    <select
                                        id={pageJumpId}
                                        className="pagination-field-select pagination-field-select--page"
                                        value={safeCurrentPage}
                                        onChange={handlePageJump}
                                        aria-label={t('menu.paginationPageJumpAria', {
                                            current: safeCurrentPage,
                                            total: safeTotalPages,
                                            defaultValue: 'Go to page, currently {{current}} of {{total}}',
                                        })}
                                    >
                                        {pageOptions.map((page) => (
                                            <option key={page} value={page}>
                                                {page}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="pagination-field-suffix">
                                        {t('menu.paginationPageOf', {
                                            total: safeTotalPages,
                                            defaultValue: 'of {{total}}',
                                        })}
                                    </span>
                                </label>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Pagination;
